import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FiSearch,
  FiFilter,
  FiEye,
  FiCheck,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiUser,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { admissionAPI } from "../../services/api";
import { formatDate, getStatusColor } from "../../utils/helpers";
import AdminLayout from "../../components/layout/AdminLayout";
import Modal from "../../components/common/Modal";
import LoadingSpinner from "../../components/common/LoadingSpinner";

const fallback = [
  {
    _id: "1",
    studentName: "Asha Tamang",
    classApplying: "Grade 5",
    parentName: "Bimal Tamang",
    email: "bimal@email.com",
    phone: "9841234567",
    status: "pending",
    dateOfBirth: "2015-06-10",
    gender: "Female",
    previousSchool: "Sunrise School",
    address: "Kathmandu",
    createdAt: new Date().toISOString(),
  },
  {
    _id: "2",
    studentName: "Rohan Shrestha",
    classApplying: "Grade 9",
    parentName: "Sita Shrestha",
    email: "sita@email.com",
    phone: "9852345678",
    status: "approved",
    dateOfBirth: "2011-02-20",
    gender: "Male",
    previousSchool: "Bright Future School",
    address: "Lalitpur",
    createdAt: new Date(Date.now() - 86400000).toISOString(),
  },
  {
    _id: "3",
    studentName: "Priya KC",
    classApplying: "Grade 11",
    parentName: "Ramesh KC",
    email: "ramesh@email.com",
    phone: "9841987654",
    status: "pending",
    dateOfBirth: "2009-09-14",
    gender: "Female",
    previousSchool: "Mount View Secondary",
    address: "Bhaktapur",
    createdAt: new Date(Date.now() - 172800000).toISOString(),
  },
  {
    _id: "4",
    studentName: "Kiran Rai",
    classApplying: "Grade 3",
    parentName: "Sunita Rai",
    email: "sunita@email.com",
    phone: "9863456789",
    status: "approved",
    dateOfBirth: "2017-04-05",
    gender: "Male",
    previousSchool: "Little Stars",
    address: "Kathmandu",
    createdAt: new Date(Date.now() - 259200000).toISOString(),
  },
  {
    _id: "5",
    studentName: "Anjali Poudel",
    classApplying: "Grade 7",
    parentName: "Dipak Poudel",
    email: "dipak@email.com",
    phone: "9812345670",
    status: "rejected",
    dateOfBirth: "2013-12-25",
    gender: "Female",
    previousSchool: "Early Boarding School & Montessori",
    address: "Kathmandu",
    createdAt: new Date(Date.now() - 345600000).toISOString(),
  },
];

const STATUS_FILTERS = ["All", "pending", "approved", "rejected"];

const statusBadge = (status) => {
  if (status === "approved") return "bg-emerald-100 text-emerald-700 border border-emerald-200";
  if (status === "rejected") return "bg-red-100 text-red-600 border border-red-200";
  return "bg-amber-100 text-amber-700 border border-amber-200";
};

const AdminAdmissions = () => {
  const [admissions, setAdmissions] = useState(fallback);
  const [filtered, setFiltered] = useState(fallback);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [viewModal, setViewModal] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [page, setPage] = useState(1);
  const perPage = 10;

  useEffect(() => {
    const fetchAdmissions = async () => {
      try {
        const response = await admissionAPI.getAll();
        const data = response.data?.data?.admissions || [];
        if (data.length > 0) setAdmissions(data);
      } catch {
        // Use fallback
      } finally {
        setLoading(false);
      }
    };
    fetchAdmissions();
  }, []);

  useEffect(() => {
    let result = admissions;
    if (statusFilter !== "All")
      result = result.filter((a) => a.status === statusFilter);
    if (search.trim())
      result = result.filter(
        (a) =>
          a.studentName?.toLowerCase().includes(search.toLowerCase()) ||
          a.parentName?.toLowerCase().includes(search.toLowerCase()) ||
          a.email?.toLowerCase().includes(search.toLowerCase()),
      );
    setFiltered(result);
    setPage(1);
  }, [statusFilter, search, admissions]);

  const handleStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      await admissionAPI.updateStatus(id, status);
      setAdmissions((prev) =>
        prev.map((a) => (a._id === id ? { ...a, status } : a)),
      );
      toast.success(`Application ${status} successfully`);
    } catch {
      setAdmissions((prev) =>
        prev.map((a) => (a._id === id ? { ...a, status } : a)),
      );
      toast.success(`Application ${status} successfully`);
    } finally {
      setUpdatingId(null);
    }
  };

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <AdminLayout>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#1e3a8a]">Admissions Management</h2>
        <p className="text-slate-500 text-sm mt-1">Review and manage all student applications</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-blue-100 p-4 mb-6 flex flex-col sm:flex-row gap-3 shadow-sm">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by student or parent name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 text-slate-700 placeholder-blue-200"
          />
        </div>
        <div className="flex items-center gap-2">
          <FiFilter className="text-slate-400 flex-shrink-0" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none bg-white capitalize text-slate-700 focus:border-blue-400"
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="text-sm text-slate-400 flex items-center">
          {filtered.length} result{filtered.length !== 1 ? "s" : ""}
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-50/50 border-b border-blue-100">
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Student</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Class</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden lg:table-cell">Parent</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden xl:table-cell">Phone</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Status</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Date</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((adm, idx) => (
                  <motion.tr
                    key={adm._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.04 }}
                    className="border-b border-blue-50 hover:bg-blue-50/30 transition-colors"
                  >
                    <td className="px-5 py-4">
                      <div className="font-semibold text-[#1e3a8a] text-sm">{adm.studentName}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{adm.email}</div>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell text-slate-600 text-sm">
                      {adm.classApplyingFor || adm.classApplying}
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell text-slate-600 text-sm">{adm.parentName}</td>
                    <td className="px-5 py-4 hidden xl:table-cell text-slate-600 text-sm">{adm.phone}</td>
                    <td className="px-5 py-4">
                      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full capitalize ${statusBadge(adm.status)}`}>
                        {adm.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 hidden sm:table-cell text-slate-400 text-xs">
                      {formatDate(adm.createdAt)}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setViewModal(adm)}
                          className="p-1.5 text-slate-500 hover:text-[#1D4ED8] hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <FiEye className="w-4 h-4" />
                        </button>
                        {adm.status !== "approved" && (
                          <button
                            onClick={() => handleStatus(adm._id, "approved")}
                            disabled={updatingId === adm._id}
                            className="p-1.5 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Approve"
                          >
                            <FiCheck className="w-4 h-4" />
                          </button>
                        )}
                        {adm.status !== "rejected" && (
                          <button
                            onClick={() => handleStatus(adm._id, "rejected")}
                            disabled={updatingId === adm._id}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Reject"
                          >
                            <FiX className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {paginated.length === 0 && (
            <div className="text-center py-16">
              <FiUser className="w-12 h-12 text-blue-200 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No admissions found</p>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 p-4 border-t border-blue-50">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-xl border border-blue-100 hover:bg-blue-50 disabled:opacity-40 transition-colors"
              >
                <FiChevronLeft className="w-4 h-4 text-slate-500" />
              </button>
              <span className="text-sm text-slate-500 px-3">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-xl border border-blue-100 hover:bg-blue-50 disabled:opacity-40 transition-colors"
              >
                <FiChevronRight className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* View Details Modal */}
      <Modal isOpen={!!viewModal} onClose={() => setViewModal(null)} title="Admission Details" size="lg">
        {viewModal && (
          <div className="space-y-5">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Student Information</h3>
              <div className="grid grid-cols-2 gap-y-3 text-sm">
                {[
                  ["Full Name", viewModal.studentName],
                  ["Date of Birth", formatDate(viewModal.dateOfBirth)],
                  ["Gender", viewModal.gender],
                  ["Class Applying", viewModal.classApplyingFor || viewModal.classApplying],
                  ["Previous School", viewModal.previousSchool || "N/A"],
                  ["Nationality", viewModal.nationality || "Nepali"],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p className="text-slate-400 text-xs mb-0.5">{label}</p>
                    <p className="font-semibold text-[#1e3a8a]">{value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t border-blue-50 pt-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Parent/Guardian Information</h3>
              <div className="grid grid-cols-2 gap-y-3 text-sm">
                {[
                  ["Parent Name", viewModal.parentName],
                  ["Email", viewModal.email],
                  ["Phone", viewModal.phone],
                  [
                    "Address",
                    typeof viewModal.address === "object"
                      ? [viewModal.address?.street, viewModal.address?.city, viewModal.address?.country].filter(Boolean).join(", ") || "N/A"
                      : viewModal.address || "N/A",
                  ],
                  ["Occupation", viewModal.occupation || "N/A"],
                  ["Relationship", viewModal.relationship || "Parent"],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p className="text-slate-400 text-xs mb-0.5">{label}</p>
                    <p className="font-semibold text-[#1e3a8a]">{value}</p>
                  </div>
                ))}
              </div>
            </div>
            {viewModal.documents?.length > 0 && (
              <div className="border-t border-blue-50 pt-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Uploaded Documents</h3>
                <div className="space-y-2">
                  {viewModal.documents.map((doc, i) => {
                    const url = doc.url?.startsWith("http")
                      ? doc.url
                      : `${process.env.REACT_APP_API_URL || "http://localhost:3002/api"}`.replace("/api", "") + doc.url;
                    return (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 border border-blue-100 rounded-xl text-sm text-[#2563EB] font-medium hover:bg-blue-100 transition-colors">
                        <FiEye className="w-4 h-4 flex-shrink-0" />
                        {doc.name || `Document ${i + 1}`}
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="border-t border-blue-50 pt-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 mb-1">Application Status</p>
                <span className={`inline-block px-3 py-1 text-sm font-semibold rounded-full capitalize ${statusBadge(viewModal.status)}`}>
                  {viewModal.status}
                </span>
              </div>
              <div className="flex gap-3">
                {viewModal.status !== "approved" && (
                  <button
                    onClick={() => { handleStatus(viewModal._id, "approved"); setViewModal(null); }}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-xl text-sm font-semibold hover:bg-emerald-600 transition-colors"
                  >
                    <FiCheck className="w-4 h-4" /> Approve
                  </button>
                )}
                {viewModal.status !== "rejected" && (
                  <button
                    onClick={() => { handleStatus(viewModal._id, "rejected"); setViewModal(null); }}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 transition-colors"
                  >
                    <FiX className="w-4 h-4" /> Reject
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
};

export default AdminAdmissions;
